import { RefObject, useEffect, useRef, useState } from "react";
import { ScriptEditorHandle } from "./ScriptEditor";
import { isSchemaScriptHtml } from "./scriptDocument";

export function useQueuedScriptGenerate(
  editorRef: RefObject<ScriptEditorHandle | null>,
  options: {
    jobId: string;
    showScript: boolean;
    generating: boolean;
    generate: (conceptId: string) => Promise<void>;
  }
) {
  const generateRef = useRef(options.generate);
  generateRef.current = options.generate;
  const jobSeen = useRef(options.jobId);
  const [pendingId, setPendingId] = useState("");

  useEffect(() => {
    if (jobSeen.current !== options.jobId) {
      jobSeen.current = options.jobId;
      if (pendingId) {
        setPendingId("");
        return;
      }
    }
    if (!options.showScript || options.generating || !pendingId) return;
    const conceptId = pendingId;
    let cancelled = false;

    const start = () => {
      if (cancelled || !editorRef.current) return false;
      setPendingId("");
      void generateRef.current(conceptId);
      return true;
    };

    if (start()) {
      return () => {
        cancelled = true;
      };
    }
    const frame = window.requestAnimationFrame(() => {
      start();
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [
    editorRef,
    options.generating,
    options.jobId,
    options.showScript,
    pendingId,
  ]);

  const request = (conceptId: string, currentId: string, script: string) => {
    if (options.generating) return;
    if (conceptId === currentId && isSchemaScriptHtml(script)) return;
    setPendingId(conceptId);
  };

  return { request };
}
